import {
  AddressBook, BookOpenText, CaretRight, ClipboardText, DownloadSimple, FolderOpen,
  DotsSixVertical, Gauge, ListChecks, NewspaperClipping, PresentationChart, SignOut, ShieldWarning, SidebarSimple, UsersThree,
} from '@phosphor-icons/react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { NavLink, Outlet } from 'react-router-dom'
import { api } from '../lib/api'
import { authClient } from '../lib/auth-client'
import type { DashboardData, SessionContext } from '../lib/types'
import { Button, LoadingScreen } from './common'
import { NotificationCenter } from './NotificationCenter'

export interface DashboardOutletContext {
  data: DashboardData
  session: SessionContext
  refresh: () => Promise<unknown>
  canEdit: boolean
  isAdmin: boolean
}

const navigation = [
  { to: '/takeover', label: 'ESS 承接能力盤點', icon: Gauge },
  { to: '/projects', label: 'Project Overview', icon: FolderOpen },
  { to: '/readiness', label: 'Project Readiness', icon: ClipboardText },
  { to: '/sow', label: 'SOW', icon: PresentationChart },
  { to: '/contacts', label: 'Contact', icon: AddressBook },
  { to: '/knowledge', label: 'Know-how', icon: BookOpenText },
  { to: '/risks', label: 'Risk', icon: ShieldWarning },
  { to: '/weekly-report', label: 'Weekly Report', icon: NewspaperClipping },
  { to: '/working-items', label: 'Working Item', icon: ListChecks },
]

const navigationPaths = navigation.map((item) => item.to)

export function mergeNavigationOrder(savedOrder: string[] | null) {
  const known = new Set(navigationPaths)
  const validSaved = (savedOrder ?? []).filter((path, index, rows) => known.has(path) && rows.indexOf(path) === index)
  return [...validSaved, ...navigationPaths.filter((path) => !validSaved.includes(path))]
}

export function moveNavigationItem(order: string[], draggedPath: string, targetPath: string) {
  if (draggedPath === targetPath || !order.includes(draggedPath) || !order.includes(targetPath)) return order
  const next = order.filter((path) => path !== draggedPath)
  next.splice(next.indexOf(targetPath), 0, draggedPath)
  return next
}

function readNavigationOrder(storageKey: string) {
  try {
    const stored = window.localStorage.getItem(storageKey)
    return mergeNavigationOrder(stored ? JSON.parse(stored) as string[] : null)
  } catch {
    return mergeNavigationOrder(null)
  }
}

export function AppShell({ session }: { session: SessionContext }) {
  const queryClient = useQueryClient()
  const dashboard = useQuery({ queryKey: ['dashboard'], queryFn: api.dashboard })
  const storageKey = `ess-dashboard-nav-order:${session.user.id}`
  const [navigationOrder, setNavigationOrder] = useState(() => readNavigationOrder(storageKey))
  const [draggingPath, setDraggingPath] = useState<string | null>(null)
  const [dragOverPath, setDragOverPath] = useState<string | null>(null)
  const [isCollapsed, setIsCollapsed] = useState<boolean>(() => {
    try {
      return window.localStorage.getItem('ess-sidebar-collapsed') === 'true'
    } catch {
      return false
    }
  })
  const orderedNavigation = useMemo(() => navigationOrder.map((path) => navigation.find((item) => item.to === path)).filter((item): item is typeof navigation[number] => Boolean(item)), [navigationOrder])

  useEffect(() => {
    setNavigationOrder(readNavigationOrder(storageKey))
  }, [storageKey])

  if (dashboard.isLoading) return <LoadingScreen />
  if (dashboard.error || !dashboard.data) {
    return <div className="center-screen error-screen"><strong>Dashboard 資料載入失敗</strong><p>{dashboard.error?.message}</p><Button onClick={() => dashboard.refetch()}>再試一次</Button></div>
  }

  const refresh = async () => dashboard.refetch()
  const logout = async () => {
    await authClient.signOut()
    queryClient.clear()
    window.location.assign('/')
  }
  const toggleCollapse = () => {
    setIsCollapsed((prev) => {
      const next = !prev
      try {
        window.localStorage.setItem('ess-sidebar-collapsed', String(next))
      } catch {
        /* Browser storage may be unavailable. */
      }
      return next
    })
  }
  const dropNavigationItem = (targetPath: string) => {
    if (!draggingPath) return
    setNavigationOrder((current) => {
      const next = moveNavigationItem(current, draggingPath, targetPath)
      try { window.localStorage.setItem(storageKey, JSON.stringify(next)) } catch { /* Browser storage may be unavailable. */ }
      return next
    })
    setDraggingPath(null)
    setDragOverPath(null)
  }

  return (
    <div className={`app-shell ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
      <aside className={`sidebar ${isCollapsed ? 'collapsed' : ''}`}>
        <div className="sidebar-brand">
          <div className="sidebar-brand-header">
            {!isCollapsed && (
              <div className="sidebar-brand-text">
                <span>WEEKLY READINESS</span>
                <strong>ESS Dashboard</strong>
                <p>Transition Command Center</p>
              </div>
            )}
            <button
              type="button"
              className="sidebar-toggle-btn"
              onClick={toggleCollapse}
              title={isCollapsed ? '展開側邊欄' : '收合側邊欄'}
              aria-label={isCollapsed ? '展開側邊欄' : '收合側邊欄'}
            >
              {isCollapsed ? <CaretRight size={18} weight="bold" /> : <SidebarSimple size={18} weight="bold" />}
            </button>
          </div>
        </div>
        <nav>
          {!isCollapsed && <div className="nav-label">Dashboard</div>}
          {isCollapsed && <div className="nav-collapsed-divider" />}
          {orderedNavigation.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              draggable={!isCollapsed}
              title={label}
              className={({ isActive }) => `nav-item ${!isCollapsed ? 'nav-item-sortable' : ''} ${isActive ? 'active' : ''} ${draggingPath === to ? 'dragging' : ''} ${dragOverPath === to ? 'drag-over' : ''}`}
              onDragStart={(event) => {
                if (isCollapsed) return
                event.dataTransfer.effectAllowed = 'move'
                event.dataTransfer.setData('text/plain', to)
                setDraggingPath(to)
              }}
              onDragOver={(event) => {
                if (isCollapsed) return
                event.preventDefault()
                event.dataTransfer.dropEffect = 'move'
                if (draggingPath && draggingPath !== to) setDragOverPath(to)
              }}
              onDragLeave={(event) => {
                if (dragOverPath === to && !event.currentTarget.contains(event.relatedTarget as Node | null)) setDragOverPath(null)
              }}
              onDrop={(event) => {
                if (isCollapsed) return
                event.preventDefault()
                dropNavigationItem(to)
              }}
              onDragEnd={() => { setDraggingPath(null); setDragOverPath(null) }}
            >
              {!isCollapsed && <DotsSixVertical className="nav-drag-handle" size={15} weight="bold" aria-hidden="true" />}
              <Icon size={isCollapsed ? 20 : 18} weight="duotone" className="nav-icon" />
              {!isCollapsed && <span className="nav-item-text">{label}</span>}
            </NavLink>
          ))}
          {session.user.role === 'admin' && (
            <>
              {!isCollapsed && <div className="nav-label secondary">Administration</div>}
              {isCollapsed && <div className="nav-collapsed-divider" />}
              <NavLink to="/admin/users" title="帳號與權限" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
                <UsersThree size={isCollapsed ? 20 : 18} weight="duotone" className="nav-icon" />
                {!isCollapsed && <span className="nav-item-text">帳號與權限</span>}
              </NavLink>
              <a className="nav-item" href="/api/export" title="下載全站完整資料 JSON 備份">
                <DownloadSimple size={isCollapsed ? 20 : 18} weight="duotone" className="nav-icon" />
                {!isCollapsed && <span className="nav-item-text">匯出完整資料</span>}
              </a>
            </>
          )}
        </nav>
        <div className="sidebar-footer">
          <NotificationCenter session={session} isCollapsed={isCollapsed} />
          <div className="sidebar-user" title={isCollapsed ? `${session.user.name} (${session.user.role})` : undefined}>
            <div className="user-avatar">{session.user.name.slice(0, 1).toUpperCase()}</div>
            {!isCollapsed && (
              <div><strong>{session.user.name}</strong><span>{session.user.role}</span></div>
            )}
            <button onClick={logout} aria-label="登出" title="登出"><SignOut size={18} /></button>
          </div>
        </div>
      </aside>
      <main className="main-surface">
        <div className="page-container">
          <Outlet context={{
            data: dashboard.data,
            session,
            refresh,
            canEdit: session.user.role === 'admin' || session.user.role === 'editor',
            isAdmin: session.user.role === 'admin',
          } satisfies DashboardOutletContext} />
        </div>
      </main>
    </div>
  )
}
